/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Field, useField } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { gameConceptInputsMaxLength } from "../../helpers/formValidation";
import { useStrings } from "../../localization/localizationHelpers";
import { AaaShippingType } from "../../models/aaaGameConceptModels";
import { getIsSavingForm } from "../../redux/selectors";
import { CheckBoxOptionEnabler } from "../CheckBoxOptionEnabler";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FieldHeader } from "../FieldHeader";
import { FormattedText } from "../FormattedText";
import { MultiLineTextField } from "../MultiLineTextField";
import { SectionTitle } from "../SectionTitle";
import * as styles from "./AaaPricingPanel.module.css";

const renderOption = (
  name: string,
  label: string,
  pricingOption: AaaShippingType,
  setSelection: (_value: React.SetStateAction<AaaShippingType>) => void,
  disabled?: boolean
) => (
  <label className={styles.option}>
    <Field
      type="radio"
      name={name}
      value={pricingOption}
      data-testid={`${name}-${pricingOption}`}
      onClick={() => setSelection(pricingOption)}
      disabled={disabled}
    />
    {label}
  </label>
);

export const AaaPricingPanel = () => {
  const [
    aaaDistInfoShippingTitle,
    aaaDistInfoShippingStrategyTitle,
    retailAndDigital,
    digitalOnlyAndF2P,
    digitalOnly,
    aaaDistInfoShippingText,
    indicateHowGameWillGenerateRevenue,
    whatRetentionMechanicsAreInGame,
    doesYourF2pGameHaveAnInGameStore,
    describeYourInGamePricePoints,
    describeWhatPlayersCanSpendOn,
    howLongUntilANewPlayerWillPay,
    canPlayersTradeOrGiftItems,
    describeHowItemsCanBeGifted,
    isThereASubscriptionFee,
    describeYourSubscriptionModel,
  ] = useStrings(
    "aaaDistInfoShippingTitle",
    "aaaDistInfoShippingStrategyTitle",
    "retailAndDigital",
    "digitalOnlyAndF2P",
    "digitalOnly",
    "aaaDistInfoShippingText",
    "indicateHowGameWillGenerateRevenue",
    "whatRetentionMechanicsAreInGame",
    "doesYourF2pGameHaveAnInGameStore",
    "describeYourInGamePricePoints",
    "describeWhatPlayersCanSpendOn",
    "howLongUntilANewPlayerWillPay",
    "canPlayersTradeOrGiftItems",
    "describeHowItemsCanBeGifted",
    "isThereASubscriptionFee",
    "describeYourSubscriptionModel"
  );
  const [field] = useField({ name: "shippingStrategy" });
  const [selection, setSelection] = useState<AaaShippingType>(field.value);
  const isSavingFormToCrm = useSelector(getIsSavingForm);

  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <SectionTitle title={aaaDistInfoShippingTitle} />
        <Row className={commonStyles.container}>
          <Column>
            <FieldHeader title={aaaDistInfoShippingStrategyTitle} isRequiredField={true} />
            <FormattedText text={aaaDistInfoShippingText} className={styles.description} />
          </Column>
        </Row>
        <div role="group" aria-labelledby="radio-options-group">
          <div className={styles.optionsGroups}>
            {renderOption(
              field.name,
              retailAndDigital,
              AaaShippingType.retailAndDigital,
              setSelection,
              isSavingFormToCrm
            )}
            {renderOption(
              field.name,
              digitalOnlyAndF2P,
              AaaShippingType.digitalOnlyF2P,
              setSelection,
              isSavingFormToCrm
            )}
            {renderOption(
              field.name,
              digitalOnly,
              AaaShippingType.digitalOnly,
              setSelection,
              isSavingFormToCrm
            )}
          </div>
        </div>
        {selection === AaaShippingType.digitalOnlyF2P && (
          <>
            <MultiLineTextField
              title={indicateHowGameWillGenerateRevenue}
              name={"howItWillGenerateMoney"}
              isRequiredField={true}
              isSaving={isSavingFormToCrm}
              maxLengthsByFieldName={gameConceptInputsMaxLength}
            />
            <MultiLineTextField
              title={whatRetentionMechanicsAreInGame}
              name={"retentionMechanics"}
              isRequiredField={true}
              isSaving={isSavingFormToCrm}
              maxLengthsByFieldName={gameConceptInputsMaxLength}
            />
            <CheckBoxOptionEnabler
              name={"hasInGameStore"}
              text={doesYourF2pGameHaveAnInGameStore}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeYourInGamePricePoints}
                name={"inGamePricePoints"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
                maxLengthsByFieldName={gameConceptInputsMaxLength}
              />
              <MultiLineTextField
                title={describeWhatPlayersCanSpendOn}
                name={"whatPlayersCanSpendOn"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
                maxLengthsByFieldName={gameConceptInputsMaxLength}
              />
              <MultiLineTextField
                title={howLongUntilANewPlayerWillPay}
                name={"timeUntilAPlayerIsPromptedToPay"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
                maxLengthsByFieldName={gameConceptInputsMaxLength}
              />
            </CheckBoxOptionEnabler>
            <CheckBoxOptionEnabler
              name={"canPlayersTradeOrGiftItems"}
              text={canPlayersTradeOrGiftItems}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeHowItemsCanBeGifted}
                name={"howItemsCanBeGifted"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
                maxLengthsByFieldName={gameConceptInputsMaxLength}
              />
            </CheckBoxOptionEnabler>
            <CheckBoxOptionEnabler
              name={"hasSubscriptionFee"}
              text={isThereASubscriptionFee}
              isSaving={isSavingFormToCrm}
            >
              <MultiLineTextField
                title={describeYourSubscriptionModel}
                name={"subscriptionModel"}
                isRequiredField={true}
                isSaving={isSavingFormToCrm}
                maxLengthsByFieldName={gameConceptInputsMaxLength}
              />
            </CheckBoxOptionEnabler>
          </>
        )}
        <FieldErrorMessage fieldName={field.name} />
      </Column>
    </Row>
  );
};
